import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Flex, FormContainer, Text } from '@wyz/ui';
import { useForm } from 'react-hook-form';
import { NumberField } from '~/common/components';
import { Trans } from '~/common/context';
import { pricingTransportItemSchema } from '~/common/schemas';
import { IImportedEan, PricingFormData } from '~/common/types';

type PricingFormProps = {
  eans: IImportedEan[];
  onCancel: () => void;
  onSubmit: (formData: PricingFormData) => void;
  isSubmitting?: boolean;
};

function PricingForm({
  eans,
  onCancel,
  onSubmit,
  isSubmitting,
}: PricingFormProps) {
  const methods = useForm<PricingFormData>({
    resolver: zodResolver(pricingTransportItemSchema),
    defaultValues: { eans: [] },
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={(formData) => {
        onSubmit(formData);
      }}
    >
      <div className='space-y-4'>
        <div className='text-center'>
          <Text heading='normal' weight='semi-bold'>
            <Trans i18nKey='app.container.order.import_modal.pricing.title' />
          </Text>
          <Text heading='sm' color='secondary-gray'>
            <Trans i18nKey='app.container.order.import_modal.pricing.warning' />
          </Text>
        </div>
        <table className='table table-light'>
          <thead>
            <tr>
              <th>{'#'}</th>
              <th style={{ width: '50%' }}>
                <Trans i18nKey='app.common.product.ean' />
              </th>
              <th>
                <Trans i18nKey='app.common.product.price' />
              </th>
            </tr>
          </thead>
          <tbody>
            {eans
              .filter((item) => item.needsPriceUpdate)
              .map((item, index) => (
                <tr key={index}>
                  <td className='bg-white'>{index + 1}</td>
                  <td className='bg-white'>
                    <Text weight='semi-bold' heading='normal'>
                      {item.ean}
                    </Text>
                  </td>
                  <td className='bg-white' align='center'>
                    <Flex>
                      <input
                        type='hidden'
                        {...methods.register(`eans.${index}.ean`)}
                        value={item.ean}
                      />
                      <NumberField
                        name={`eans[${index}].price`}
                        size='sm'
                        label={''}
                        min={0}
                        max={Infinity}
                        withKnobs={false}
                      />
                    </Flex>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <Flex className='space-x-2'>
          <Button
            id='cancel-pricing'
            variant='secondary'
            onClick={onCancel}
            fullWidth
          >
            <Trans i18nKey='app.common.button.cancel' />
          </Button>
          <Button
            id='submit-pricing'
            variant='primary'
            type='submit'
            isLoading={isSubmitting}
            fullWidth
          >
            <Trans i18nKey='app.common.buttons.validate' />
          </Button>
        </Flex>
      </div>
    </FormContainer>
  );
}

export default PricingForm;
